'use client';

import { useMemo } from 'react';
import { Settings2, UserCircle2 } from 'lucide-react';

import type { AccountMenuGroup } from '@kit/accounts/personal-account-dropdown';
import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useFeatureFlags } from '@kit/feature-flags';
import { DEFAULT_BRAND } from '@kit/shared/brand';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { useUser } from '@kit/supabase/hooks/use-user';
import { JWTUserData } from '@kit/supabase/types';

import {
  consolidatedAccountSections,
  type ConsolidatedNavFlags,
} from '~/config/navigation/nav-sections';
import pathsConfig from '~/config/paths.config';
import type { Brand } from '~/lib/brand';

const paths = {
  home: pathsConfig.app.home,
};

/**
 * Plain-scalar flags the server layout passes so this client container can
 * build the consolidated "My Profile" / "Account" menu groups itself (icons
 * and section factories are not RSC-serializable, so they stay client-side).
 * When omitted, the dropdown renders without the consolidated groups —
 * exactly the pre-consolidation menu (marketing header, team surfaces).
 */
export type AccountDropdownNavFlags = ConsolidatedNavFlags;

function buildMenuGroups(
  flags: AccountDropdownNavFlags,
  brand: Brand,
): AccountMenuGroup[] {
  return consolidatedAccountSections(flags, brand).map((section) => ({
    i18nKey: section.i18nKey,
    icon:
      section.i18nKey === 'common:routes.myProfile' ? UserCircle2 : Settings2,
    items: section.items,
  }));
}

export function ProfileAccountDropdownContainer(props: {
  user?: JWTUserData | null;
  showProfileName?: boolean;

  /** Trigger width — 'w-full' suits sidebar footers (legacy default);
   * pass a compact value in the top bar so the chip doesn't stretch the
   * right-hand cluster. */
  className?: string;

  account?: {
    id: string | null;
    name: string | null;
    picture_url: string | null;
  };

  /** Resolved public-profile path for dancers (`/<handle>`); renders the
   * "My Profile" menu entry. Omitted for non-dancers (BAL-364). */
  dancerProfileHref?: string | null;

  /** When set, folds the consolidated "My Profile" / "Account" nav groups
   * into the dropdown (2026-07-05 sidebar consolidation). */
  navFlags?: AccountDropdownNavFlags;

  /** Current brand — filters Flow-only `/home/*` settings out of the consolidated
   * groups on Ballee (and vice versa) so they don't render then bounce.
   * Defaults to the shared default brand when omitted. */
  brand?: Brand;

  /** App-specific menu entries appended after the groups (e.g. the
   * super-admin impersonate submenu). */
  extraItems?: React.ReactNode;
}) {
  const signOut = useSignOut();
  const user = useUser(props.user);
  const userData = user.data;
  const featureFlags = useFeatureFlags();

  const features = {
    enableThemeToggle: featureFlags.enableThemeToggle,
  };

  const navFlags = props.navFlags;
  const brand = props.brand ?? DEFAULT_BRAND;
  const menuGroups = useMemo(
    () => (navFlags ? buildMenuGroups(navFlags, brand) : undefined),
    [navFlags, brand],
  );

  if (!userData) {
    return null;
  }

  return (
    <PersonalAccountDropdown
      className={props.className ?? 'w-full'}
      paths={paths}
      features={features}
      user={userData}
      account={props.account}
      signOutRequested={() => signOut.mutateAsync()}
      showProfileName={props.showProfileName}
      // The consolidated My Profile group already leads with the public-page
      // link (dancerProfileSlug); keep the standalone entry only when the
      // groups are not rendered.
      dancerProfileHref={menuGroups ? null : props.dancerProfileHref}
      menuGroups={menuGroups}
      extraItems={props.extraItems}
    />
  );
}
